/**
 * TIMELINE — the workspace's centre column when no tool is open.
 *
 * The reference layout draws four stacked waveform lanes. Four lanes would be four
 * stems, and this app has no stem audio — Split Stems is a timer that produces
 * text files. So rather than draw four decorative lanes and imply a multitrack
 * editor that does not exist, this shows ONE lane for the track, labelled with what
 * it is, and states plainly that stem lanes appear once stems are real.
 *
 * That is the honest version of the reference: a timeline that is the song, with
 * the room for lanes reserved and the reason for their absence given.
 *
 * What is real here: the seeded waveform (stable per song), the playhead, the
 * section markers derived from the lyric sheet's own [Verse]/[Chorus] headings, and
 * the song's details. Nothing on this panel is invented data.
 *
 * SIMULATED. The waveform is a seeded PRNG, not amplitude, and the playhead is
 * driven by the interval in useSimulatedPlayback. The lane is not editable and
 * the timeline is not seekable, because there is no audio to seek.
 */
import { Music } from 'lucide-react';
import { useLiveDateLabel } from '../../hooks/useLiveDateLabel';
import Disclosure from './Disclosure';
import { seededHeights } from '../../lib/waveform';
import { parseLength } from '../../lib/formatting';
import { smoothingSeconds } from '../../lib/playback';
import { lyricBlocks, songSections } from '../../lib/sections';
import { c } from '../../styles/theme';
import type { Song } from '../../types/domain';

export interface TimelineProps {
  song: Song;
  playing: boolean;
  /** True when this song is loaded, running or paused. Defaults to `playing`. */
  active?: boolean;
  elapsed: number;
  /**
   * Playback rate, so the playhead's transition covers one tick rather than a fixed
   * second. Defaults to 1×, which is the behaviour this had before speed was wired up.
   */
  speed?: number;
}

/** Label/value pairs for the details strip, in the drawer's order. */
function detailRows(song: Song, dateLabel: string): Array<[string, string | number]> {
  const rows: Array<[string, string | number] | null> = [
    song.genre ? ['Genre', song.genre] : null,
    ['BPM', song.bpm],
    ['Mood', song.mood],
    ['Duration', song.length],
    ['Language', song.language],
    ['Generated', dateLabel],
    ['AI model', song.aiModel],
  ];
  return rows.filter((row): row is [string, string | number] => row !== null);
}

function timedSections(song: Song): Array<{ name: string; at: number }> {
  const blocks = lyricBlocks(song.lyrics);
  const totalSec = parseLength(song.length);
  if (!(totalSec > 0) || blocks.length === 0 || !song.lyricSectionTimings?.length) return [];

  return song.lyricSectionTimings
    .filter(
      (entry) =>
        Number.isInteger(entry.block) &&
        entry.block >= 0 &&
        entry.block < blocks.length &&
        Number.isFinite(entry.startSec) &&
        entry.startSec >= 0
    )
    .slice()
    .sort((a, b) => a.startSec - b.startSec || a.block - b.block)
    .map((entry) => ({
      name: entry.heading ?? blocks[entry.block]?.name ?? `Section ${entry.block + 1}`,
      at: Math.max(0, Math.min(1, entry.startSec / totalSec)),
    }));
}

export default function Timeline({
  song,
  playing,
  active = playing,
  elapsed,
  speed = 1,
}: TimelineProps) {
  const dateLabel = useLiveDateLabel(song.createdAt, song.date);
  const totalSec = parseLength(song.length);
  // `active`, not `playing`: the store's `elapsed` belongs to this song for as long as it
  // is the loaded one, and a pause is not a reason to redraw the playhead at zero.
  const pos = active ? elapsed : 0;
  const pct = totalSec ? Math.min(100, (pos / totalSec) * 100) : 0;
  const sections = timedSections(song);
  const approximateSections = sections.length === 0 ? songSections(song) : [];

  return (
    <section className="lyx-ws-timeline" aria-label="Timeline">
      <p className="lyx-ws-eyebrow">Timeline</p>

      {/* ---- section markers ----
          Prefer real section timings when the backend has them; otherwise fall back to
          the older text-share approximation. Both are labelled for what they are, so the
          timeline never claims a precision the song does not carry. */}
      {(sections.length > 0 || approximateSections.length > 0) && (
        <div
          className="lyx-ws-sections"
          aria-label={sections.length > 0 ? 'Timed song sections' : 'Approximate song sections'}
          style={{ borderBottom: `1px solid ${c.border}` }}
        >
          {(sections.length > 0 ? sections : approximateSections).map((s) => (
            <span
              key={`${s.name}-${s.at}`}
              className="lyx-ws-section"
              style={{ left: `${s.at * 100}%`, color: c.text2 }}
            >
              <span style={{ borderLeft: `1px solid ${c.border}` }} />
              {s.name}
            </span>
          ))}
        </div>
      )}

      {/* ---- the lane ---- */}
      <div
        className="lyx-ws-lane"
        style={{ background: c.surface1, border: `1px solid ${c.border}` }}
      >
        <div className="lyx-ws-lane-head" style={{ borderRight: `1px solid ${c.border}` }}>
          <Music size={12} color={c.text2} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 11, color: c.text2 }}>Master</span>
        </div>

        <div className="lyx-ws-lane-body">
          {/* Taller and denser than the transport's waveform: this is the one place
              the track is shown as a shape rather than as a control. */}
          <div className="lyx-ws-lane-wave" aria-hidden="true">
            {seededHeights(song.id, 96).map((h, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  minWidth: 1,
                  // Mirrored around the centre line, which is what makes it read as
                  // a waveform rather than as a bar chart.
                  height: `${Math.round((h / 26) * 100)}%`,
                  borderRadius: 1,
                  background: playing ? c.accentFill : c.borderStrong,
                  transition: 'background 200ms',
                  opacity: playing ? 0.9 : 1,
                }}
              />
            ))}
          </div>

          {/* The playhead. Transitioned over exactly one tick to match the clock, which
              is why the duration comes from `speed` rather than being a fixed second:
              at 2× the lane is crossed in half the wall-clock time and a 1s transition
              would leave the playhead lagging the position readout for the whole track.
              Shown while active rather than while playing, so pausing leaves it where you
              stopped instead of making it vanish and snap back to the start on resume. */}
          <div
            className="lyx-ws-playhead"
            style={{
              left: `${pct}%`,
              background: c.accentFill,
              opacity: active ? 1 : 0,
              transition: `left ${smoothingSeconds(speed)}s linear, opacity 200ms`,
            }}
          />
        </div>
      </div>

      {/* <PrototypeNotice style={{ marginTop: 12 }}>
        One lane, because there is one track. Separate stem lanes appear here once Split Stems
        produces real audio — today it exports a text placeholder.
      </PrototypeNotice> */}

      {/* ---- reference sections ----
          Collapsed, and deliberately so even though this column has the room: the
          centre of a production workspace should show the track, not a metadata
          table. These are the drawer's own two disclosures, with the same titles,
          so nothing became less reachable than it was. */}
      <div style={{ marginTop: 18 }}>
        <Disclosure title="Song details">
          {/* A grid of label/value pairs rather than the prototype's nine bordered
              tiles. They are facts about the track, so they read as a caption. */}
          <dl className="lyx-ws-details">
            {detailRows(song, dateLabel).map(([label, val]) => (
              <div key={label} className="lyx-ws-detail">
                <dt style={{ fontSize: 10, color: c.text2, textTransform: 'uppercase' }}>
                  {label}
                </dt>
                <dd
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    margin: '2px 0 0',
                    color: c.text1,
                    fontFamily: typeof val === 'number' ? 'IBM Plex Mono' : 'Inter',
                  }}
                >
                  {val}
                </dd>
              </div>
            ))}
          </dl>
        </Disclosure>

        <Disclosure title="Prompt used">
          <p style={{ fontSize: 12, color: c.text2, margin: 0, lineHeight: 1.7 }}>
            {song.prompt || '—'}
          </p>
        </Disclosure>
      </div>
    </section>
  );
}
